import React, { useRef, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  Divider
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 3, 3),
    maxWidth: 980,
    margin: '0 auto',
    borderRadius: 20,
    background: 'linear-gradient(135deg,#ffffff 0%, #fafbff 100%)',
    boxShadow: '0 10px 28px -4px rgba(32,40,52,0.10), 0 4px 12px -2px rgba(32,40,52,0.06)',
    position: 'relative',
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 220px)',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: -140,
      left: -140,
      width: 320,
      height: 320,
      background: 'radial-gradient(circle at 55% 55%, rgba(66,133,244,0.20), rgba(66,133,244,0) 70%)',
      borderRadius: '50%',
      filter: 'blur(4px)',
      opacity: 0.7,
      pointerEvents: 'none'
    }
  },
  rootCompact: {
    padding: theme.spacing(2, 3, 3),
    margin: '0 auto'
  },
  titleWrap: { marginBottom: theme.spacing(2) },
  title: {
    fontWeight: 600,
    letterSpacing: '-0.5px',
    background: 'linear-gradient(90deg,#1d1f27 0%, #2f3b55 100%)',
    WebkitBackgroundClip: 'text',
    color: 'transparent'
  },
  subtitle: {
    fontSize: 14,
    color: '#5f6b7b',
    fontWeight: 400,
    marginTop: theme.spacing(0.5)
  },
  sectionLabel: {
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: '0.12em',
    fontWeight: 600,
    color: '#7d8694',
    marginBottom: theme.spacing(1.2)
  },
  sectionBox: {
    background: '#ffffff',
    border: '1px solid #edf0f5',
    borderRadius: 16,
    padding: theme.spacing(2.5, 3, 0.5),
    position: 'relative',
    '& + &': {
      marginTop: theme.spacing(3)
    }
  },
  groupNote: {
    fontSize: 11.5,
    color: '#88939f',
    marginTop: theme.spacing(1.2)
  },
  gridSection: {
    marginBottom: theme.spacing(1)
  },
  field: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: '#fff',
      transition: 'box-shadow .25s, border-color .25s',
      '& fieldset': {
        borderColor: '#dfe3ea'
      },
      '&:hover fieldset': {
        borderColor: '#b8c2cf'
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(66,133,244,0.25)',
        '& fieldset': {
          borderColor: '#4285f4'
        }
      }
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#1d60d6'
    },
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '& input[type=number]': { MozAppearance: 'textfield' }
  },
  // dateWide: on joue plutôt sur la grille (md=5) qu'un style spécial,
  metalPill: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12 // homogène avec les autres
    }
  },
  actionsBar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid #e7ebf0'
  },
  primaryBtn: {
    borderRadius: 14,
    padding: '10px 20px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '.3px',
    background: 'linear-gradient(90deg,#1a73e8 0%,#1967d2 100%)',
    boxShadow: '0 4px 14px -2px rgba(26,115,232,0.35)',
    '&:hover': {
      background: 'linear-gradient(90deg,#1b6fdc 0%,#155fc2 100%)'
    }
  },
  resetBtn: {
    borderRadius: 14,
    textTransform: 'none',
    fontWeight: 500
  },
  subtleDivider: {
    margin: theme.spacing(1.5, 0, 2),
    background: 'linear-gradient(90deg,rgba(0,0,0,0) 0%, #dfe4ea 50%, rgba(0,0,0,0) 100%)',
    height: 1,
    border: 'none'
  }
}));

// Helper pour today yyyy-mm-dd
const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const GoldForm = ({
  fournisseurs = [], // liste d'entités (fournisseurs ou clients)
  metaux = [], // liste des métaux { id_metal, description }
  defaultMetalId = 1,
  onSubmit,
  initialValuesData = null,
  title = 'Formulaire Or',
  editing = false,
  onCancelEdit,
  compact = false,
  entityLabel = 'Fournisseur',
  perteReadOnly = false,
  autoPerteValue = null,
  sortieLabel = 'Sortie Or 750',
  // suppression de la gestion vieil or
}) => {
  const classes = useStyles();

  // refs pour navigation au clavier (mouseless)
  const dateRef = useRef(null);
  const fournisseurRef = useRef(null);
  const perteRef = useRef(null);
  const designationRef = useRef(null);
  const metalRef = useRef(null);
  const sortieRef = useRef(null); // sera maintenant après entrée
  const entreeRef = useRef(null); // entrée avant sortie dans l'UI

  const refs = useMemo(() => [dateRef, fournisseurRef, perteRef, designationRef, metalRef, entreeRef, sortieRef], []);

  // Focus auto initial sur la date (après montage pour garantir présence DOM)
  useEffect(() => {
    // double stratégie pour fiabilité (certaines implémentations input date retardent le rendu)
    const focusFn = () => {
      if (dateRef.current && typeof dateRef.current.focus === 'function') {
        try { dateRef.current.focus(); } catch (_) { /* ignore */ }
      }
    };
    focusFn();
    const t = setTimeout(focusFn, 50);
    const t2 = setTimeout(focusFn, 150);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, []);

  const focusNext = (currentIndex) => {
    const next = refs[currentIndex + 1];
    if (next && next.current) {
      try {
        if (next.current.focus) next.current.focus();
        // Sélection du contenu si possible
        if (next.current.select) next.current.select();
        // Cas Autocomplete: input interne
        if (!next.current.select && next.current.querySelector) {
          const inner = next.current.querySelector('input');
          if(inner){ inner.focus(); inner.select && inner.select(); }
        }
      } catch (_) { /* ignore */ }
    }
  };

  const baseInitialValues = {
    date: todayYYYYMMDD(),
    fournisseur: null,
    perte: autoPerteValue ?? '',
    designation: '',
    entreeOrFin: '',
    sortieOr750: '',
    metal: defaultMetalId,
  // vieilOr supprimé
  };
  const initialValues = initialValuesData ? { ...baseInitialValues, ...initialValuesData } : baseInitialValues;

  const normalizeNum = (o) => {
    if(o===''||o===null||o===undefined) return null; const n = parseFloat(String(o).replace(',','.')); return isNaN(n)? null : n;
  };
  const validationSchema = Yup.object({
    date: Yup.string().required('Date requise'),
    fournisseur: Yup.mixed().required(`${entityLabel} requis`),
    perte: Yup.number().transform((v,o)=> normalizeNum(o)).typeError('Doit être un nombre').min(0, '>= 0').max(100, '<= 100').required('% Perte requis'),
    designation: Yup.string().required('Désignation requise').min(2),
    entreeOrFin: Yup.number()
      .transform((v,o)=> normalizeNum(o))
      .typeError('Doit être un nombre')
      .min(0, '>= 0')
      .nullable(true),
    sortieOr750: Yup.number()
      .transform((v,o)=> normalizeNum(o))
      .typeError('Doit être un nombre')
      .min(0, '>= 0')
      .nullable(true),
  }).test('au-moins-un', 'Remplir au moins un des champs (Entrée / Sortie / Vieil Or)', function (values) {
    if (!values) return false;
    const { entreeOrFin, sortieOr750 } = values;
    const hasEntree = entreeOrFin !== null && entreeOrFin !== undefined && entreeOrFin !== '' && parseFloat(String(entreeOrFin).replace(',','.')) !== 0;
    const hasSortie = sortieOr750 !== null && sortieOr750 !== undefined && sortieOr750 !== '' && parseFloat(String(sortieOr750).replace(',','.')) !== 0;
    if (!hasEntree && !hasSortie) {
      return this.createError({ path: 'entreeOrFin', message: 'Remplir au moins un champ' });
    }
    return true;
  });

  // Perte auto: Formik a enableReinitialize + setFieldValue dans render; pas besoin de token supplémentaire.

  // Limitation & format: 4 chiffres avant, 2 après, conserver virgule (xxxx,xx)
  const limitNumeric = (raw) => {
    if(raw===''||raw===null||raw===undefined) return '';
    let v = String(raw).replace(/\s+/g,'');
    v = v.replace(/[^0-9.,]/g,'');
    const firstSepIndex = v.search(/[.,]/);
    let sepChar = null;
    if(firstSepIndex !== -1){
      sepChar = v[firstSepIndex];
      const head = v.slice(0, firstSepIndex); // avant séparateur
      let tail = v.slice(firstSepIndex+1).replace(/[.,]/g,'');
      v = head + sepChar + tail;
    }
    const parts = v.split(/[.,]/);
    let int = parts[0] || '';
    let dec = parts[1] || '';
    if(int.length>4) int=int.slice(0,4);
    if(dec.length>2) dec=dec.slice(0,2);
    if(sepChar === null) return int;
    return dec.length ? `${int}${sepChar}${dec}` : `${int}${sepChar}`;
  };

  return (
  <Paper className={clsx(classes.root, compact && classes.rootCompact)} elevation={0}>
      <div className={classes.titleWrap}>
  <Typography variant="h4" className={classes.title} style={{display:'flex',alignItems:'center',minHeight:40,flexWrap:'nowrap',whiteSpace:'nowrap',maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis'}}>
          <span>{title}</span>
          <span style={{ width: editing ? 'auto':'0px', overflow:'hidden', transition:'width .25s', marginLeft: editing ? 8 : 0, whiteSpace:'nowrap', fontSize:'0.70em', fontWeight:600, letterSpacing:'0.5px', padding: editing ? '4px 10px':0, borderRadius:12, background: editing ? '#e3edfb':'transparent', color:'#1a4f9c' }}> {editing ? 'ÉDITION' : ''}</span>
        </Typography>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          const norm = (v)=> (v===''||v===null||v===undefined)? '' : String(v).replace(',','.');
          let payload = { ...values };
          payload.perte = norm(values.perte);
          payload.entreeOrFin = norm(values.entreeOrFin);
          if (values.metal === 1) {
            payload.sortieOr750 = norm(values.sortieOr750);
            // sortie_or_fin = conversion pure (sortie 750 * 0.75) sans la perte
            // Seulement si sortieOr750 est renseigné
            if (values.sortieOr750 && norm(values.sortieOr750) !== '') {
              const sortie750Num = parseFloat(norm(values.sortieOr750));
              payload.sortieOrFin = (sortie750Num * 0.75).toFixed(3);
            } else {
              payload.sortieOrFin = '';
            }
          } else {
            payload.sortieMetal = norm(values.sortieOr750);
            // Calcul de la perte en valeur pour les autres métaux
            if (values.entreeOrFin && values.sortieOr750) {
              payload.perteMetal = (parseFloat(norm(values.entreeOrFin)) - parseFloat(norm(values.sortieOr750))).toFixed(2);
            } else {
              payload.perteMetal = '';
            }
          }
          if (onSubmit) onSubmit(payload);
          // Réinitialisation des poids après création (pas en édition)
          if(!editing){
            actions.resetForm({ values: {
              ...values,
              entreeOrFin: '',
              sortieOr750: '',
              perte: autoPerteValue ?? '',
              designation: '',
              // garder date et métal, garder fournisseur sélectionné pour enchaîner
            }});
            setTimeout(()=>{ if(entreeRef.current){ try{ entreeRef.current.focus(); }catch(_){ } } }, 30);
          }
          actions.setSubmitting(false);
        }}
      >
            {({ values, errors, touched, handleChange, setFieldValue, isSubmitting, submitForm, resetForm }) => {
              // 1. Logique Vieil Or (prioritaire) pour éviter boucle avec autoPerteValue
              // Auto-perte standard
              if (perteReadOnly && autoPerteValue !== null && autoPerteValue !== undefined && values.perte !== autoPerteValue) {
                setFieldValue('perte', autoPerteValue, false);
              }

              // Handlers personnalisés pour éviter boucles et gérer la logique demandée
              const handleEntreeChangeCustom = (e) => {
                const val = limitNumeric(e.target.value);
                setFieldValue('entreeOrFin', val);
              };
              // const handleSortieChangeCustom = (e) => {
              //   const val = limitNumeric(e.target.value);
              //   setFieldValue('sortieOr750', val);
              // };
              return (
          <Form>
            <Grid container spacing={2} className={classes.gridSection}>
              <Grid item xs={12}>
                <div className={classes.sectionLabel}>Informations générales</div>
              </Grid>
              <Grid item xs={12} sm={5} md={5}>
                <TextField
                  inputRef={dateRef}
                  name="date"
                  label="Date"
                  type="date"
                  value={values.date}
                  onChange={(e)=>{ setFieldValue('date', e.target.value); }}
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  autoFocus
                  InputLabelProps={{ shrink: true }}
                  onFocus={e=>{ e.target.select && e.target.select(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusNext(0); } }}
                  error={touched.date && Boolean(errors.date)}
                  helperText={(touched.date && errors.date) ? errors.date : (() => { const d = values.date; if(!d) return ''; const [y,m,dd] = d.split('-'); return `Affiché: ${dd}/${m}/${y}` })()}
                />
              </Grid>

              <Grid item xs={12} sm={7} md={7}>
                <Autocomplete
                  className={classes.field}
                  options={fournisseurs}
                  autoHighlight
                  clearOnEscape
                  getOptionLabel={(option) => (option && (option.label || option.raison_soc || option.nom || option.name || ''))}
                  getOptionSelected={(option, value) => {
                    if(!option || !value) return false;
                    const oid = option.id ?? option.four_id ?? option.client_id;
                    const vid = value.id ?? value.four_id ?? value.client_id;
                    return String(oid) === String(vid);
                  }}
                  value={values.fournisseur}
                  onChange={(e, value) => {
                    setFieldValue('fournisseur', value);
                    if (value && (value.perte !== undefined && value.perte !== null)) {
                      setFieldValue('perte', value.perte);
                    }
                    // Règle spéciale: fournisseur id=12 => métal id=3 sinon métal par défaut (uniquement si pas en édition)
                    if (!editing && entityLabel === 'Fournisseur') {
                      const selectedId = value ? (value.id ?? value.four_id ?? value.client_id) : null;
                      if (String(selectedId) === '12') {
                        setFieldValue('metal', 3);
                      } else if (values.metal !== defaultMetalId) {
                        setFieldValue('metal', defaultMetalId);
                      }
                    }
                    // Après sélection, passer directement au champ % Perte (navigation fluide)
                    setTimeout(()=>{ if(perteRef.current) { try{perteRef.current.focus();}catch(_){/* ignore */} } },0);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={fournisseurRef}
                      label={entityLabel}
                      variant="outlined"
                      size="small"
                      onFocus={e=>{ const el=e.target; el.select && el.select(); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { if (!values.fournisseur && fournisseurs && fournisseurs.length) { const first = fournisseurs[0]; setFieldValue('fournisseur', first); if (first && (first.perte !== undefined && first.perte !== null)) { setFieldValue('perte', first.perte); } } e.preventDefault(); focusNext(1); } }}
                      error={touched.fournisseur && Boolean(errors.fournisseur)}
                      helperText={touched.fournisseur && errors.fournisseur}
                    />
                  )}
                />
              </Grid>
              {/* Champ perte */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  inputRef={perteRef}
                  name="perte"
                  label="% Perte"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.,]?[0-9]*"
                  value={values.perte}
                  onChange={e => {
                    const val = e.target.value;
                    setFieldValue('perte', val);
                    // Recalculer sortieOrFin si sortieOr750 est rempli
                    const sortieNum = parseFloat(values.sortieOr750?.replace?.(',', '.') || 0);
                    if (sortieNum > 0) {
                      const perteNum = parseFloat(val.replace(',', '.')) || 0;
                      const sortieFinBase = sortieNum * 0.75;
                      const perteGrammes = sortieFinBase * (perteNum / 100);
                      const sortieFin = sortieFinBase + perteGrammes;
                      setFieldValue('sortieOrFin', sortieFin ? sortieFin.toFixed(2) : '');
                    }
                  }}
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  InputProps={perteReadOnly ? { readOnly: true } : undefined}
                  onFocus={e=>{ e.target.select && e.target.select(); }}
                  onKeyDown={(e) => { if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if (e.key === 'Enter') { e.preventDefault(); focusNext(2); } }}
                  error={touched.perte && Boolean(errors.perte)}
                  helperText={touched.perte && errors.perte}
                />
              </Grid>
              {/* Désignation */}
              <Grid item xs={12} sm={8} md={8}>
                <TextField
                  inputRef={designationRef}
                  name="designation"
                  label="Désignation"
                  value={values.designation}
                  onChange={e => setFieldValue('designation', e.target.value.toUpperCase().replace(/\s+/g, ''))}
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  onFocus={e=>{ e.target.select && e.target.select(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusNext(3); } }}
                  error={touched.designation && Boolean(errors.designation)}
                  helperText={touched.designation && errors.designation}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider className={classes.subtleDivider} />
              </Grid>
              <Grid item xs={12}>
                <div className={classes.sectionLabel}>Mouvements Or</div>
              </Grid>
              {/* Nouvelle disposition: Métal + Sortie (ligne 1), Entrée + Vieil Or (ligne 2) */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={metaux}
                  getOptionLabel={(o)=> (o && (o.description || o.label || ''))}
                  value={metaux.find(m => String(m.id_metal) === String(values.metal)) || null}
                  onChange={(e,val)=>{ setFieldValue('metal', val ? (val.id_metal) : defaultMetalId); setTimeout(()=>{ if(entreeRef.current){ try{entreeRef.current.focus();}catch(_){}} },0); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={metalRef}
                      label="Métal"
                      variant="outlined"
                      size="small"
                      className={clsx(classes.field, classes.metalPill)}
                      helperText="Choisir le métal"
                      onFocus={e=>{ const el=e.target; el.select && el.select(); }}
                      onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); focusNext(4); } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  inputRef={entreeRef}
                  name="entreeOrFin"
                  label={values.metal === 1 ? 'Entrée Or / Fin' : 'Entrée Métal'}
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.,]?[0-9]*"
                  value={values.entreeOrFin ?? ''}
                  onChange={handleEntreeChangeCustom}
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  onFocus={e=>{ e.target.select && e.target.select(); }}
                  onKeyDown={(e) => { if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if (e.key === 'Enter') { e.preventDefault(); focusNext(5); } }}
                  error={touched.entreeOrFin && Boolean(errors.entreeOrFin)}
                  helperText={touched.entreeOrFin && errors.entreeOrFin}
                />
              </Grid>
              {/* Ligne séparée pour les sorties Or */}
              {values.metal === 1 ? (
                <Grid item xs={12} container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <TextField
                      inputRef={sortieRef}
                      name="sortieOr750"
                      label={sortieLabel}
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.,]?[0-9]*"
                      value={values.sortieOr750 ?? ''}
                      onChange={e => {
                        const val = limitNumeric(e.target.value);
                        setFieldValue('sortieOr750', val);
                        // Calcul automatique du champ sortieOrFin (sans modifier la perte)
                        const perteNum = parseFloat(values.perte) || 0;
                        const sortieNum = parseFloat(val.replace(',', '.')) || 0;
                        const sortieFinBase = sortieNum * 0.75;
                        const perteGrammes = sortieFinBase * (perteNum / 100);
                        const sortieFin = sortieFinBase + perteGrammes;
                        setFieldValue('sortieOrFin', sortieFin ? sortieFin.toFixed(2) : '');
                      }}
                      className={classes.field}
                      variant="outlined"
                      size="small"
                      onFocus={e=>{ e.target.select && e.target.select(); }}
                      onKeyDown={(e) => { if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if (e.key === 'Enter') { e.preventDefault(); submitForm(); } }}
                      error={touched.sortieOr750 && Boolean(errors.sortieOr750)}
                      helperText={touched.sortieOr750 && errors.sortieOr750}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name="sortieOrFin"
                      label="Sortie Or Fin Avec Perte"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.,]?[0-9]*"
                      value={values.sortieOrFin ?? ''}
                      onChange={e => {
                        const val = limitNumeric(e.target.value);
                        setFieldValue('sortieOrFin', val);
                        // Si rempli manuellement, recalculer la perte
                        const sortieNum = parseFloat(values.sortieOr750?.replace(',', '.') || 0);
                        const sortieFinAvecPerteNum = parseFloat(val.replace(',', '.') || 0);
                        if (sortieNum > 0) {
                          // Sortie Or Fin Avec Perte = Sortie Or 750 * 0.75 + perte en grammes
                          // => perte en grammes = Sortie Or Fin Avec Perte - (Sortie Or 750 * 0.75)
                          // => perte % = (perte en grammes / (Sortie Or 750 * 0.75)) * 100
                          const sortieFinBase = sortieNum * 0.75;
                          const perteGrammes = sortieFinAvecPerteNum - sortieFinBase;
                          const perteCalc = (perteGrammes / sortieFinBase) * 100;
                          setFieldValue('perte', perteCalc >= 0 ? perteCalc.toFixed(2) : '0.00');
                        }
                      }}
                      className={classes.field}
                      variant="outlined"
                      size="small"
                      onFocus={e=>{ e.target.select && e.target.select(); }}
                      error={touched.sortieOrFin && Boolean(errors.sortieOrFin)}
                      helperText={touched.sortieOrFin && errors.sortieOrFin}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    inputRef={sortieRef}
                    name="sortieOr750"
                    label="Sortie Métal"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={values.sortieOr750 ?? ''}
                    onChange={e => {
                      const val = limitNumeric(e.target.value);
                      setFieldValue('sortieOr750', val);
                    }}
                    className={classes.field}
                    variant="outlined"
                    size="small"
                    onFocus={e=>{ e.target.select && e.target.select(); }}
                    onKeyDown={(e) => { if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if (e.key === 'Enter') { e.preventDefault(); submitForm(); } }}
                    error={touched.sortieOr750 && Boolean(errors.sortieOr750)}
                    helperText={touched.sortieOr750 && errors.sortieOr750}
                  />
                </Grid>
              )}
            {/* Fermeture correcte du Grid principal */}

            </Grid>

            <Box className={classes.actionsBar}>
              {editing && (
                <Button className={classes.resetBtn} variant="text" onClick={() => { if(onCancelEdit) onCancelEdit(); }}>
                  Annuler édition
                </Button>
              )}
              <Button className={classes.resetBtn} variant="outlined" onClick={() => { resetForm(); }}>
                Réinitialiser
              </Button>
              <Button className={classes.primaryBtn} color="primary" variant="contained" type="submit" disabled={isSubmitting}>
                {editing ? 'Mettre à jour' : 'Enregistrer'}
              </Button>
            </Box>

          </Form>
        );}}
      </Formik>
    </Paper>
  );
};

GoldForm.propTypes = {
  fournisseurs: PropTypes.array,
  onSubmit: PropTypes.func,
  compact: PropTypes.bool,
  entityLabel: PropTypes.string,
  perteReadOnly: PropTypes.bool,
  autoPerteValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sortieLabel: PropTypes.string
};

export default GoldForm;
